import { Dropdown } from '@/components/Dropdown/Dropdown';
import ThreeDots from '@/images/ThreeDotsBlack.svg';
import { useAdvancedEditorContext } from '@/pages/AdvancedEditor/context/useAdvancedEditorContext';
import { getPageProperty, PolotnoStoreActivePageKeys } from '@/pages/AdvancedEditor/hooks';
import { polotnoTypes } from '@/pages/AdvancedEditor/types';
import { checkIsAvatarOrAvatarGroup, checkPolotnoType } from '@/pages/AdvancedEditor/utils';
import { useFlag } from '@/services/featureFlags';
import styled from '@emotion/styled';
import { MenuList, ListItemIcon as MuiListItemIcon } from '@mui/material';
import { useState } from 'react';
import { AlignMenuItem } from '../options/align/AlignMenuItem';
import { AlignMenuItems } from '../options/align/AlignOptionsList';
import { ArrangeOptionsList, useArrangeOptionsList } from '../options/arrange/ArrangeOptionsList';
import { DeleteItem } from '../options/DeleteItem';
import { DuplicateMenuItem } from '../options/DuplicateMenuItem';
import { FitToPageMenuItem } from '../options/FitToPageMenuItem';
import { LockMenuItem } from '../options/LockMenuItem';
import { StyledDivider } from '../StyledDivider';
import { StyledIconButton } from '../StyledIconButton';

const StyledList = styled(MenuList)`
    padding: 0;

    &:focus {
        outline: none;
    }
`;

const VerticalDivider = styled.div`
    width: 1px;
    background-color: ${({ theme }) => theme.colors.black[300]};
    height: 20px;
`;

export const ListItemIcon = styled(MuiListItemIcon)`
    min-width: 3.2rem;
`;

export const OptionsContextualMenu = () => {
    const { store } = useAdvancedEditorContext();
    const isThereAnySelectedLockedElement = getPageProperty(store.activePage, PolotnoStoreActivePageKeys.isThereAnySelectedLockedElement);
    const selectedElements = store.selectedElements;
    const editorLockUnlockEnabled = useFlag('editor_lock_unlock_enabled', false);
    const { items: arrangeInnerItems } = useArrangeOptionsList();

    const [closed, setClosed] = useState<boolean>(true);

    const handleCloseMenu = () => {
        setClosed(true);
    };

    const handleOpenMenu = () => {
        setClosed(false);
    };

    const nonAvatarItems = selectedElements.every(element => !checkIsAvatarOrAvatarGroup(element))
        ? [{ label: <DuplicateMenuItem closeMenu={handleCloseMenu} /> }]
        : [];

    const fitToPageItem =
        selectedElements.length === 1 &&
        (checkPolotnoType(selectedElements[0], [polotnoTypes.Image, polotnoTypes.Svg, polotnoTypes.Video, polotnoTypes.Avatar]) ||
            checkIsAvatarOrAvatarGroup(selectedElements[0]))
            ? [{ label: <FitToPageMenuItem closeMenu={handleCloseMenu} /> }]
            : [];

    const lockItems = editorLockUnlockEnabled ? [{ label: <LockMenuItem closeMenu={handleCloseMenu} /> }] : [];

    const items = [
        /* TODO: Add in future PR */
        // { label: <CutMenuItem closeMenu={handleCloseMenu} /> },
        // { label: <CopyMenuItem closeMenu={handleCloseMenu} /> },
        // { label: <PasteMenuItem closeMenu={handleCloseMenu} /> },
        ...fitToPageItem,
        ...nonAvatarItems,
        { label: <DeleteItem inPopoverMenu closeMenu={handleCloseMenu} /> },
        ...lockItems,
        { label: <StyledDivider /> },
        { label: <ArrangeOptionsList inPopoverMenu anchorOrigin={{ vertical: 'top', horizontal: 'right' }} />, items: arrangeInnerItems },
        {
            label: <AlignMenuItem />,
            items: AlignMenuItems,
        },
    ];

    return !isThereAnySelectedLockedElement ? (
        <>
            <VerticalDivider />
            <Dropdown
                width="200px"
                closed={closed}
                onOpen={handleOpenMenu}
                onClose={handleCloseMenu}
                trigger={<StyledIconButton src={ThreeDots} ariaLabel="options" isActive={!closed} />}
                items={items}
                renderMenuWrapper={children => <StyledList>{children}</StyledList>}
            />
        </>
    ) : null;
};
